import { useEffect, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Checkbox } from '../../components/ui/checkbox';
import { Label } from '../../components/ui/label';
import DictationInput from '../../components/DictationInput';
import { toast } from 'sonner';
import {
  ClipboardList, CheckCircle, FileText, Home, Shield, User,
  ChevronDown, ChevronRight, Plus, Send, Clock, AlertTriangle
} from 'lucide-react';

const CATEGORY_LABELS = {
  intake: { label: 'Intake & Orientation', icon: ClipboardList, color: 'text-cyan-600' },
  documents: { label: 'Document Collection', icon: FileText, color: 'text-violet-600' },
  assessment: { label: 'Assessment & Planning', icon: Shield, color: 'text-amber-600' },
  move_in: { label: 'Move-In', icon: Home, color: 'text-emerald-600' },
};

export default function OnboardingPage() {
  const { axiosAuth } = useAuth();
  const api = axiosAuth();
  const [residents, setResidents] = useState([]);
  const [selectedResident, setSelectedResident] = useState(null);
  const [onboarding, setOnboarding] = useState(null);
  const [checklistTemplate, setChecklistTemplate] = useState([]);
  const [loading, setLoading] = useState(false);
  const [newNote, setNewNote] = useState('');
  const [expandedCategory, setExpandedCategory] = useState('intake');

  useEffect(() => {
    api.get('/housing/residents').then(r => setResidents(r.data)).catch(() => {});
    api.get('/our-square/onboarding/checklist-template').then(r => setChecklistTemplate(r.data)).catch(() => {});
  }, []);

  const loadOnboarding = async (residentId) => {
    setSelectedResident(residentId);
    setLoading(true);
    try {
      const res = await api.get(`/our-square/onboarding/${residentId}`);
      setOnboarding(res.data);
    } catch {
      toast.error('Failed to load onboarding data');
    } finally {
      setLoading(false);
    }
  };

  const toggleChecklistItem = async (itemKey, completed) => {
    try {
      const res = await api.put(`/our-square/onboarding/${selectedResident}`, {
        checklist_item: itemKey,
        completed,
      });
      setOnboarding(res.data);
      toast.success(completed ? 'Item completed' : 'Item unchecked');
    } catch {
      toast.error('Failed to update');
    }
  };

  const signHouseRules = async () => {
    try {
      const res = await api.put(`/our-square/onboarding/${selectedResident}`, { house_rules_signed: true });
      setOnboarding(res.data);
      toast.success('House rules acknowledged');
    } catch {
      toast.error('Failed to update');
    }
  };

  const addDocNote = async () => {
    if (!newNote.trim()) return toast.error('Enter a note');
    try {
      const res = await api.put(`/our-square/onboarding/${selectedResident}`, { add_note: newNote.trim() });
      setOnboarding(res.data);
      setNewNote('');
      toast.success('Note added');
    } catch {
      toast.error('Failed to add note');
    }
  };

  const checklist = onboarding?.checklist || {};
  const completedCount = Object.values(checklist).filter(v => v.completed).length;
  const totalItems = checklistTemplate.length;
  const progressPct = totalItems ? Math.round((completedCount / totalItems) * 100) : 0;
  const resident = residents.find(r => r.id === selectedResident);

  const categorizedItems = {};
  checklistTemplate.forEach(item => {
    if (!categorizedItems[item.category]) categorizedItems[item.category] = [];
    categorizedItems[item.category].push(item);
  });

  // Resident List View
  if (!selectedResident) {
    return (
      <div className="space-y-6" data-testid="onboarding-page">
        <div>
          <h1 className="text-2xl font-bold" style={{ fontFamily: 'Manrope' }}>Resident Onboarding</h1>
          <p className="text-sm text-muted-foreground mt-1">Manage move-in checklists, house rules, documentation, and needs assessments</p>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {residents.length === 0 ? (
            <Card className="border-0 shadow-sm rounded-2xl col-span-full">
              <CardContent className="p-8 text-center">
                <User size={40} className="mx-auto text-slate-300 mb-3" />
                <p className="text-muted-foreground">No residents found. Approve an intake application to get started.</p>
              </CardContent>
            </Card>
          ) : residents.map(r => (
            <Card
              key={r.id}
              className="border-0 shadow-sm rounded-2xl cursor-pointer hover:shadow-md hover:-translate-y-0.5 transition-all"
              onClick={() => loadOnboarding(r.id)}
              data-testid={`onboarding-resident-${r.id}`}
            >
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-cyan-100 flex items-center justify-center text-sm font-bold text-cyan-700">
                    {(r.user_name || r.full_name || '?')[0]}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm truncate">{r.user_name || r.full_name || 'Unknown'}</p>
                    <div className="flex gap-1 mt-0.5">
                      <Badge variant="outline" className="text-[10px]">{r.room_label || 'No Room'}</Badge>
                      <Badge className={`text-[10px] border-0 ${r.status === 'active' ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}>{r.status}</Badge>
                    </div>
                  </div>
                  <ChevronRight size={14} className="text-muted-foreground" />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  // Onboarding Detail View
  return (
    <div className="space-y-5" data-testid="onboarding-detail">
      <Button variant="ghost" size="sm" className="rounded-full gap-1" onClick={() => { setSelectedResident(null); setOnboarding(null); }} data-testid="onboarding-back">
        <ChevronRight size={14} className="rotate-180" /> Back to Residents
      </Button>

      {loading ? (
        <Card className="border-0 shadow-sm rounded-2xl">
          <CardContent className="p-8 text-center">
            <div className="w-8 h-8 border-3 border-cyan-500 border-t-transparent rounded-full animate-spin mx-auto mb-3" />
            <p className="text-sm text-muted-foreground">Loading onboarding data...</p>
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Resident Header */}
          <Card className="border-0 shadow-sm rounded-2xl bg-gradient-to-r from-cyan-50 to-teal-50">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-cyan-200 flex items-center justify-center text-lg font-bold text-cyan-800">
                    {(resident?.user_name || '?')[0]}
                  </div>
                  <div>
                    <h2 className="font-bold text-base">{resident?.user_name || 'Unknown'}</h2>
                    <div className="flex gap-1.5 mt-0.5">
                      <Badge variant="outline" className="text-[10px]">{resident?.room_label || 'No Room'}</Badge>
                      <Badge className="bg-cyan-100 text-cyan-700 border-0 text-[10px]">Move-in: {resident?.move_in_date || 'TBD'}</Badge>
                    </div>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-2xl font-bold text-cyan-700">{progressPct}%</p>
                  <p className="text-[10px] text-muted-foreground">{completedCount}/{totalItems} Complete</p>
                </div>
              </div>
              <div className="w-full bg-cyan-100 rounded-full h-2.5 mt-3">
                <div className="bg-cyan-600 h-2.5 rounded-full transition-all" style={{ width: `${progressPct}%` }} />
              </div>
            </CardContent>
          </Card>

          {/* Move-In Checklist by Category */}
          <Card className="border-0 shadow-sm rounded-2xl" data-testid="onboarding-checklist">
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2"><ClipboardList size={16} /> Move-In Checklist</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {Object.entries(categorizedItems).map(([cat, items]) => {
                const catInfo = CATEGORY_LABELS[cat] || { label: cat, icon: ClipboardList, color: 'text-slate-600' };
                const CatIcon = catInfo.icon;
                const isExpanded = expandedCategory === cat;
                const catCompleted = items.filter(i => checklist[i.key]?.completed).length;
                return (
                  <div key={cat} className="rounded-xl border overflow-hidden">
                    <div
                      className="flex items-center justify-between px-3 py-2.5 cursor-pointer hover:bg-slate-50 transition-colors"
                      onClick={() => setExpandedCategory(isExpanded ? null : cat)}
                    >
                      <div className="flex items-center gap-2">
                        <CatIcon size={15} className={catInfo.color} />
                        <span className="text-sm font-medium">{catInfo.label}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge className={catCompleted === items.length ? 'bg-emerald-50 text-emerald-700 border-0 text-[10px]' : 'bg-slate-100 text-slate-500 border-0 text-[10px]'}>
                          {catCompleted}/{items.length}
                        </Badge>
                        {isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                      </div>
                    </div>
                    {isExpanded && (
                      <div className="px-3 pb-3 border-t space-y-1.5 pt-2">
                        {items.map(item => {
                          const status = checklist[item.key] || {};
                          return (
                            <div key={item.key} className={`flex items-center gap-3 p-2 rounded-lg transition-colors ${status.completed ? 'bg-emerald-50/50' : 'hover:bg-slate-50'}`}>
                              <Checkbox
                                checked={status.completed || false}
                                onCheckedChange={v => toggleChecklistItem(item.key, v)}
                                data-testid={`checklist-${item.key}`}
                              />
                              <div className="flex-1">
                                <p className={`text-sm ${status.completed ? 'line-through text-muted-foreground' : ''}`}>{item.label}</p>
                                {status.completed && status.completed_by_name && (
                                  <p className="text-[10px] text-muted-foreground">
                                    Completed by {status.completed_by_name} on {status.completed_at?.split('T')[0]}
                                  </p>
                                )}
                              </div>
                              {status.completed && <CheckCircle size={14} className="text-emerald-500" />}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </CardContent>
          </Card>

          {/* House Rules Acknowledgment */}
          <Card className="border-0 shadow-sm rounded-2xl" data-testid="onboarding-house-rules">
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2"><Shield size={16} /> House Rules Acknowledgment</CardTitle>
            </CardHeader>
            <CardContent>
              {onboarding?.house_rules_signed ? (
                <div className="flex items-center gap-3 p-3 rounded-xl bg-emerald-50 border border-emerald-200">
                  <CheckCircle size={18} className="text-emerald-600" />
                  <div>
                    <p className="text-sm font-medium text-emerald-800">House Rules Acknowledged</p>
                    <p className="text-[10px] text-emerald-600">Signed on {onboarding.house_rules_signed_at?.split('T')[0]}</p>
                  </div>
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="flex items-center gap-3 p-3 rounded-xl bg-amber-50 border border-amber-200">
                    <AlertTriangle size={18} className="text-amber-600" />
                    <p className="text-sm text-amber-800">House rules have not been acknowledged yet.</p>
                  </div>
                  <Button className="bg-cyan-600 hover:bg-cyan-700 text-white rounded-full gap-1" onClick={signHouseRules} data-testid="sign-house-rules">
                    <CheckCircle size={14} /> Mark as Acknowledged
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Staff Documentation Notes */}
          <Card className="border-0 shadow-sm rounded-2xl" data-testid="onboarding-notes">
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2"><FileText size={16} /> Documentation Notes</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex gap-2">
                <DictationInput
                  value={newNote}
                  onChange={v => setNewNote(v)}
                  placeholder="Add a documentation note (speak or type)..."
                  rows={2}
                  showCleanup={false}
                  className="flex-1"
                  data-testid="onboarding-note-input"
                />
                <Button className="bg-cyan-600 hover:bg-cyan-700 text-white rounded-xl shrink-0" onClick={addDocNote} data-testid="onboarding-note-add">
                  <Plus size={16} />
                </Button>
              </div>
              {(onboarding?.documentation_notes || []).length > 0 ? (
                <div className="space-y-1.5 max-h-[300px] overflow-y-auto">
                  {(onboarding.documentation_notes || []).slice().reverse().map(note => (
                    <div key={note.id} className="p-2.5 rounded-xl bg-slate-50 border text-xs">
                      <div className="flex items-center justify-between mb-1">
                        <span className="font-medium">{note.author_name}</span>
                        <span className="text-muted-foreground">{note.created_at?.split('T')[0]}</span>
                      </div>
                      <p className="text-slate-700">{note.text}</p>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground text-center py-2">No notes yet.</p>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
